import { ICache } from "csi/typings";
import { ReactionStore } from "csi/utility/mobxUtility";
import _ from "lodash";
import React, { createContext, RefObject, useContext, useEffect, useMemo, useRef, useState } from "react";
export { default as useResizeObserver } from "use-resize-observer";

// NOTE: useResizeObserver returns the content height/width rather than border height/width, so 
// be careful that you are not relying on the correct height/width when using on an element with borders

/**
 * Hook to get access to local cache
 */
export const CacheContext = createContext<ICache>(undefined);
export const useCache = (): ICache => useContext(CacheContext);

type StoreType = {
	/** handler to clean up  */
	onCleanup?: (instance: StoreType) => void;
}

/**
 * Hook to perform async initialization (since useEffect cannot be async itself)
 */
export function useEffectAsync(asyncInitializer: () => Promise<void>, dependencies: any[] = []): void {
	useEffect(() => {
		asyncInitializer();
	}, dependencies);
}

/**
 * Hook to use to add event listener to given element
 * Reference: https://usehooks-typescript.com/react-hook/use-event-listener
 */
export function useEventListener<T extends HTMLElement = HTMLDivElement>(
	eventName: string,
	handler: (event: Event) => void,
	element?: RefObject<T>,
): void {
	// Create a ref that stores handler
	const savedHandler = useRef<(event: Event) => void>();

	useEffect(() => {
		// Define the listening target
		const targetElement: T | Window = element?.current || window;

		if (!(targetElement && targetElement.addEventListener)) {
			return;
		}

		// Update saved handler if necessary
		if (savedHandler.current !== handler) {
			savedHandler.current = handler;
		}

		// Create event listener that calls handler function stored in ref
		const eventListener = (event: Event): void => {
			// eslint-disable-next-line no-extra-boolean-cast
			if (!!savedHandler?.current) {
				savedHandler.current(event);
			}
		};

		targetElement.addEventListener(eventName, eventListener);

		// Remove event listener on cleanup
		return () => {
			targetElement.removeEventListener(eventName, eventListener);
		};
	}, [eventName, element, handler]);
}

/**
 * Hook to track a value immediately using React state, but debounce the call to a callback function;
 * an example use of this is for entering search text with a controller component - the component
 * needs to show the entered text immediately, but processing the search text can be delayed
 */
type SetValueFunction<T> = (value: T) => void;

export function useDebounce<T = string>(value: T, setValue: SetValueFunction<T>, duration: number, dependencies?: any[]): [T, SetValueFunction<T>] {
	const [internalValue, setInternalValue] = useState<T>(value);
	const debouncedSetValue = useMemo(() => _.debounce(setValue, duration), [setValue, duration]);
	useEffect(() => {
		if (dependencies) {
			setInternalValue(value);
		}
	}, dependencies);

	return [internalValue, (value: T): void => {
		setInternalValue(value);
		debouncedSetValue(value);
	}];
}

/**
 * Hook to use when testing for hovering over given element 
 * Reference: https://usehooks-typescript.com/react-hook/use-hover
 */
export function useHover<T extends HTMLElement = HTMLElement>(elementRef: RefObject<T>): boolean {
	const [value, setValue] = useState<boolean>(false);
	const handleMouseEnter = (): void => setValue(true);
	const handleMouseLeave = (): void => setValue(false);

	useEffect(() => {
		const node = elementRef?.current;

		if (node) {
			node.addEventListener("mouseenter", handleMouseEnter);
			node.addEventListener("mouseleave", handleMouseLeave);

			return () => {
				node.removeEventListener("mouseenter", handleMouseEnter);
				node.removeEventListener("mouseleave", handleMouseLeave);
			};
		}
	}, [elementRef]);

	return value;
}

interface UseProgressiveImg {
	isLoading: boolean;
	src: string;
}

/**
 * Hook to use a high quality image after it loads.
 * Reference: https://benhoneywill.com/progressive-image-loading-with-react-hooks/
 */
export const useProgressiveImg = (lowQualitySrc: string, highQualitySrc: string): UseProgressiveImg => {
	const [src, setSrc] = useState(lowQualitySrc);
	const img = new Image();
	useEffect(() => {
		setSrc(lowQualitySrc);
		img.src = highQualitySrc;
		img.onload = (e): void => {
			setSrc(highQualitySrc);
		};
		return () => {
			// cancel image loading on unmount
			img.src = "";
		};
	}, [lowQualitySrc, highQualitySrc]);
	return { src, isLoading: src === lowQualitySrc };
};

interface IntersectionObserverArgs extends IntersectionObserverInit {
	/** stop observing the intersection after first occurrence */
	freezeOnceVisible?: boolean;

	/** callback to call when intersection is discovered */
	onIntersection?: () => void;
}

/**
 * Hook to use when testing for intersection (of viewport) with given element
 * Reference: https://usehooks-typescript.com/react-hook/use-intersection-observer
 */
export function useIntersectionObserver(
	elementRef: RefObject<Element>,
	{
		freezeOnceVisible = false,
		onIntersection = null,
		root = null,
		rootMargin = "0%",
		threshold = 0,
	}: IntersectionObserverArgs,
): IntersectionObserverEntry | undefined {
	const [entry, setEntry] = useState<IntersectionObserverEntry>();
	const frozen = entry?.isIntersecting && freezeOnceVisible;

	const updateEntry = ([entry]: IntersectionObserverEntry[]): void => {
		setEntry(entry);

		if (entry.isIntersecting) {
			onIntersection?.();
		}
	};

	useEffect(() => {
		const node = elementRef?.current; // DOM Ref

		if (frozen || !node) {
			return;
		}

		const observerParams = { threshold, root, rootMargin };
		const observer = new IntersectionObserver(updateEntry, observerParams);

		observer.observe(node);

		return () => observer.disconnect();
	}, [elementRef, threshold, root, rootMargin, frozen]);

	return entry;
};

interface IntersectionObserverAsyncArgs extends Omit<IntersectionObserverArgs, "onIntersection"> {
	onIntersection: () => Promise<void>;
}

/**
 * Hook to use when callback is async, and the intersection item can potentially stay in view after load
 */
export function useIntersectionObserverAsync(
	elementRef: RefObject<Element>,
	{
		onIntersection,
		root = null,
		rootMargin = "0%",
		threshold = 0,
	}: IntersectionObserverAsyncArgs,
): IntersectionObserverEntry | undefined {
	const [entry, setEntry] = useState<IntersectionObserverEntry>();
	const isFetching = useRef<boolean>(false);

	useEffect(() => {
		const observerCallback = async ([entry]: IntersectionObserverEntry[]): Promise<void> => {
			setEntry(entry);

			if (entry.isIntersecting && !isFetching.current) {
				isFetching.current = true;
				observer.unobserve(entry.target);
				await onIntersection();
				isFetching.current = false;
				observer.observe(entry.target);
			}
		};

		const observer = new IntersectionObserver(observerCallback, {
			root,
			rootMargin,
			threshold,
		});

		const node = elementRef.current;
		if (node) {
			observer.observe(node);
		}

		return () => {
			if (node) {
				observer.unobserve(node);
			}
			observer.disconnect();
		};
	}, [elementRef, onIntersection, root, rootMargin, threshold]);

	return entry;
}

/**
 * Hook to use when creating a async/sync local store that extends ReactionStore so that reactions are cleaned up when done
 */
export function useLocalReactionStore<T extends ReactionStore>(initializer: () => Promise<T> | T): T {
	const [store, setStore] = useState<T>(null);

	useEffect(() => {
		// since the call below is async, need to declare this variable to make sure get the reference to cleanup function, otherwise, would keep null due to JavaScript Closure
		let ref = null;

		async function init(): Promise<void> {
			const asyncStore = await initializer();
			setStore(asyncStore);
			ref = asyncStore;
		}

		init();

		return () => {
			return ref?.cleanupReactions();
		};
	}, []);

	return store;
}

/**
 * Hook to use when creating a local store that doesn't need to be an observable, but doesn't need reactions
 */
export function useLocalStore<T extends StoreType>(initializer: () => Promise<T> | T, deps?: React.DependencyList): T {
	const [store, setStore] = useState<T>(null);
	let ref: T = null;

	useEffect(() => {
		async function init(): Promise<void> {
			const asyncStore = ref = await initializer();
			setStore(asyncStore);
		}

		init();

		return () => {
			// clean up something after the store is destroyed
			return ref?.onCleanup?.(ref);
		};
	}, deps || []);

	return store;
}

/**
 * Hook to use when creating a local store that doesn't need its initializer to be async
 */
export function useSyncLocalStore<T>(initializer: () => T): T {
	const [store, setStore] = useState<T>(null);
	let returnStore = store;

	if (!returnStore) {
		returnStore = initializer();
		setStore(returnStore);
	}

	return returnStore;
}

/** Hook to use when a local store needs to recreate once dependency changes */
export function useLocalStoreWithDep<T>(initializer: () => T | T, deps: React.DependencyList): T {
	const [store, setStore] = useState<T>(null);

	useEffect(() => {
		const newStore = initializer();
		setStore(newStore);
	}, deps);

	return store;
}